import React, { useRef } from 'react'
import Box from '@mui/material/Box'
import { useMutation } from 'react-query'
import {
  CnvPlotDataType,
  CnvPlotType,
  getCnvChromPlot,
  getCnvPlot,
  getExomedepthPlots,
  getGlsExomedepthPlotData,
  getGlsVariants,
  GetSampleCnvQcDto,
  getVariantsByCNV,
  VariantChrom
} from '@/@core/services/app.service'
import { useRouter } from 'next/router'
import {
  Autocomplete,
  Backdrop,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputAdornment,
  OutlinedInput,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { NObject } from '@/@core/components/filter-variants'
import Slider, { SliderThumb } from '@mui/material/Slider'
import { styled } from '@mui/material/styles'

import dynamic from 'next/dynamic'
import Scatters from './Scatter'
import { Scatter } from '@antv/g2plot'
import CnvAllScatter from './CnvAllScatter'
import CnvScatter from './CnvScatter'
import { CloseFullscreenSharp, OpenInFullSharp } from '@mui/icons-material'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { DownloadImage } from '@/@core/utils/utils'
import { Magnify } from 'mdi-material-ui'
import PositionedSnackbar from '@/@core/components/message'

const Plot = dynamic(() => import('react-plotly.js'), { ssr: false })
type AirbnbThumbComponentProps = React.HTMLAttributes<unknown>

function AirbnbThumbComponent(props: AirbnbThumbComponentProps) {
  const { children, ...other } = props

  return (
    <SliderThumb {...other}>
      {children}

      <span className="airbnb-bar" />

      <span className="airbnb-bar" />

      <span className="airbnb-bar" />
    </SliderThumb>
  )
}
const AirbnbSlider = styled(Slider)(({ theme }) => ({
  color: '#3a8589',
  height: 3,
  padding: '13px 0',
  '& .MuiSlider-thumb': {
    height: 27,
    width: 27,
    backgroundColor: '#fff',
    border: '1px solid currentColor',
    '&:hover': {
      boxShadow: '0 0 0 8px rgba(58, 133, 137, 0.16)'
    },
    '& .airbnb-bar': {
      height: 9,
      width: 1,
      backgroundColor: 'currentColor',
      marginLeft: 1,
      marginRight: 1
    }
  },
  '& .MuiSlider-track': {
    height: 3
  },
  '& .MuiSlider-rail': {
    color: theme.palette.mode === 'dark' ? '#bfbfbf' : '#d8d8d8',
    opacity: theme.palette.mode === 'dark' ? undefined : 1,
    height: 3
  }
}))

const CnvHG19QcPlot = React.forwardRef(
  (
    props: {
      onlyChrom: boolean
      row?: any
      cnvRow?: any
      cnvSampleId?: any
      analysisInfo: any
    },
    ref
  ) => {
    const hg19_len: NObject = {
      '1': 249250621,
      '2': 243199373,
      '3': 198022430,
      '4': 191154276,
      '5': 180915260,
      '6': 171115067,
      '7': 159138663,
      '8': 146364022,
      '9': 141213431,
      '10': 135534747,
      '11': 135006516,
      '12': 133851895,
      '13': 115169878,
      '14': 107349540,
      '15': 102531392,
      '16': 90354753,
      '17': 81195210,
      '18': 78077248,
      '19': 59128983,
      '20': 63025520,
      '21': 48129895,
      '22': 51304566,
      X: 155270560,
      Y: 59373566
    }
    const router = useRouter()
    const { t } = useTranslation()

    const { id } = router.query
    const plotlyRef = useRef<any>(null)
    const plotlyImgRef = useRef<any>(null)
    const cnvQcPlotRef = useRef<any>(null)
    const messageRef = React.useRef<any>()
    const [containerId, setContainerId] = React.useState('cnv-plot-container')
    const [geneName, setGeneName] = React.useState('')
    const [plotlyImgSrc, setPlotlyImgSrc] = React.useState('')
    const [plotlyImgChrom, setPlotlyImgChrom] = React.useState('')

    const [chrom, setChrom] = React.useState('1')
    const [SV_start, setSVStart] = React.useState(0)
    const [SV_end, setSVEnd] = React.useState(0)
    const [cnvData, setCnvData] = React.useState<{
      plotAllData: string
      plotData: {
        chrom: string
        plot: string
      }[]
    }>({
      plotAllData: '',
      plotData: []
    })
    const [open, setOpen] = React.useState(false)

    const handleClose = () => {
      setOpen(false)
    }

    const handleOpen = (flag: boolean) => {
      setOpen(flag)
    }

    const affectId = React.useMemo(() => {
      if (
        props.analysisInfo &&
        props.analysisInfo.samples &&
        Array.isArray(props.analysisInfo.samples)
      ) {
        const samples = props.analysisInfo.samples
        const affected_child = samples.find(
          (i: any) => i.type === 'affected_child'
        )
        if (affected_child) {
          return affected_child.id
        } else return id
      } else return id
    }, [props.analysisInfo])

    const fetchCnvChromPlot = useMutation({
      mutationFn: async () => {
        return await getCnvChromPlot((props.cnvSampleId ?? affectId) as string)
      },
      onSuccess: (data) => {
        if (data) {
          setCnvData(data as any)
        }
      }
    })

    const fetchExomedepthPlots = useMutation({
      mutationFn: async () => {
        return await getExomedepthPlots(
          (props.cnvSampleId ?? affectId) as string
        )
      },
      onSuccess: (data) => {
        if (data && data.plots) {
          setCnvData({
            plotAllData: '',
            plotData: data.plots.map((i: any) => {
              return {
                chrom: i.chromosome,
                plot: i.url
              }
            })
          })
        }
      }
    })

    const fetchCnvByGene = useMutation({
      mutationFn: async (gene: string) => {
        console.log(props.analysisInfo.pipelineVersion)
        if (props.analysisInfo.pipelineVersion) {
          return await getGlsVariants({
            parquetType: 'cnv_exomedepth',
            variants: {
              id: props.cnvSampleId ?? id,
              source: 'samples',
              offset: 0,
              limit: 30,
              filters: [
                {
                  field: 'gene',
                  op: 'contains',
                  value: [gene]
                }
              ],
              sort_by: []
            }
          })
        } else {
          const query = {
            pageNum: 1,
            pageSize: 30,
            filter: { geneNameIn: [gene] },
            sort: {}
          }

          if (props && props.row && props.row.gene) {
            query.filter = { geneNameIn: [gene], cnvType: 'exome_depth' } as any
          }

          return await getVariantsByCNV(query, (props.cnvSampleId ?? id) as any)
        }
      },
      onSuccess: (data) => {
        if (
          data &&
          data.data &&
          data.data.list &&
          Array.isArray(data.data.list) &&
          data.data.list.length
        ) {
          const row = data.data.list[0]

          setChrom(row.chrom)

          setSVEnd(row.SV_end ?? row.end)

          setSVStart(row.SV_start ?? row.start)
        } else if (
          data &&
          data.items &&
          Array.isArray(data.items) &&
          data.items.length
        ) {
          const row = data.items[0]

          setChrom(row.chrom)

          setSVEnd(row.SV_end ?? row.end)

          setSVStart(row.SV_start ?? row.start)
        } else {
          messageRef?.current?.handleClick(
            t('Gene info not found. Please check again'),
            'warning'
          )
        }
      }
    })

    const maxHeight = React.useMemo(() => {
      const mains = document.getElementsByTagName('main')
      if (mains && Array.from(mains).length) {
        const main = Array.from(mains)[0]

        return main.clientHeight - 200
      } else return 800
    }, [])

    React.useEffect(() => {
      if (props.analysisInfo.pipelineVersion) {
        //
        fetchExomedepthPlots.mutateAsync()
      } else fetchCnvChromPlot.mutateAsync()
    }, [])

    React.useEffect(() => {
      if (props && props.cnvRow && props.cnvRow.chrom) {
        setChrom(props.cnvRow.chrom)

        setSVEnd(props.cnvRow.SV_end ?? props.cnvRow.end)

        setSVStart(props.cnvRow.SV_start ?? props.cnvRow.start)
      } else if (props && props.row && props.row.gene) {
        const gene = props.row.gene

        setGeneName(gene)

        fetchCnvByGene.mutateAsync(gene)

        cnvQcPlotRef.current?.searchFn && cnvQcPlotRef.current?.searchFn()
      } else {
        setSVEnd(100000)
      }

      //
    }, [props, props.row, props.cnvRow])

    return (
      <Box
        sx={{
          overflow: 'auto',
          maxHeight: maxHeight,
          bgcolor: 'white'
        }}
      >
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={open}
          onClick={handleClose}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        <Card>
          <CardContent
            sx={{
              bgcolor: 'rgb(242,244,245)',
              display: 'flex',
              flexDirection: 'column-reverse'
            }}
            ref={plotlyRef}
          >
            <Stack>
              {Object.keys(hg19_len)
                .filter((key) => key == chrom)
                .map((key) => {
                  if (SV_start || SV_end)
                    return (
                      <CnvQcPlot
                        plotlyRef={plotlyRef}
                        handleOpen={handleOpen}
                        chrom={key}
                        key={key}
                        ref={cnvQcPlotRef}
                        containerId={containerId}
                        start={SV_start}
                        end={SV_end}
                        analysisInfo={props.analysisInfo}
                        cnvSampleId={props.cnvSampleId}
                      />
                    )
                  else return ''
                })}
            </Stack>

            <Stack
              sx={{
                py: 4,
                alignItems: 'center',
                justifyContent: 'center'
              }}
              direction="row"
              spacing={4}
            >
              <FormControl
                fullWidth
                sx={{ m: 1, margin: '10px 0', width: 250 }}
              >
                <OutlinedInput
                  id="outlined-adornment-amount"
                  size="small"
                  onChange={(e) => {
                    setGeneName(e.target.value)
                  }}
                  placeholder={`${t('Please enter gene')}`}
                  onKeyDown={(e) => {
                    //

                    if (e.key === 'Enter') {
                      e.preventDefault()

                      fetchCnvByGene.mutateAsync(e.currentTarget.value)
                    }
                  }}
                  value={geneName}
                />
              </FormControl>

              <Autocomplete
                size="small"
                value={chrom}
                onChange={(event: any, newValue: string | null) => {
                  if (newValue) setChrom(newValue)
                }}
                id="controllable-states-demo"
                options={Object.keys(hg19_len)}
                sx={{ width: 300 }}
                renderInput={(params) => {
                  return <TextField {...params} label={t('chromosome')} />
                }}
                renderOption={(props, option) => {
                  const { ...optionProps } = props

                  return (
                    <Typography {...optionProps}>{`${t(
                      'chromosome'
                    )} ${option}`}</Typography>
                  )
                }}
              />

              <Button
                variant="contained"
                onClick={() => {
                  if (geneName) {
                    if (SV_start === 0) fetchCnvByGene.mutateAsync(geneName)
                    else
                      cnvQcPlotRef.current?.searchFn &&
                        cnvQcPlotRef.current?.searchFn()
                  } else
                    cnvQcPlotRef.current?.searchFn &&
                      cnvQcPlotRef.current?.searchFn()

                  //
                }}
              >
                {t('Search')}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {!props.onlyChrom && cnvData && cnvData.plotAllData ? (
          <Box>
            <img src={cnvData.plotAllData} />

            <Box width={'100%'} textAlign={'end'} pr={10}>
              <ButtonGroup variant="contained" aria-label="Basic button group">
                {plotlyImgSrc ? (
                  <Button
                    size="small"
                    onClick={() => {
                      const documentCopy = document as any

                      if (documentCopy.exitFullscreen) {
                        documentCopy.exitFullscreen()
                      } else if (documentCopy.mozCancelFullScreen) {
                        // Firefox

                        documentCopy.mozCancelFullScreen()
                      } else if (documentCopy.webkitExitFullscreen) {
                        // Chrome, Safari and Opera

                        documentCopy.webkitExitFullscreen()
                      } else if (documentCopy.msExitFullscreen) {
                        // IE/Edge

                        documentCopy.msExitFullscreen()
                      }

                      setPlotlyImgSrc('')

                      setPlotlyImgChrom('')
                    }}
                  >
                    <CloseFullscreenIcon />
                  </Button>
                ) : (
                  <Button
                    size="small"
                    onClick={() => {
                      if (plotlyImgRef.current.requestFullscreen) {
                        plotlyImgRef.current.requestFullscreen()
                      } else if (plotlyImgRef.current.mozRequestFullScreen) {
                        // Firefox

                        plotlyImgRef.current.mozRequestFullScreen()
                      } else if (plotlyImgRef.current.webkitRequestFullscreen) {
                        // Chrome, Safari & Opera

                        plotlyImgRef.current.webkitRequestFullscreen()
                      } else if (plotlyImgRef.current.msRequestFullscreen) {
                        // IE/Edge

                        plotlyImgRef.current.msRequestFullscreen()
                      }

                      setPlotlyImgSrc(cnvData.plotAllData)
                    }}
                  >
                    <OpenInFullIcon />
                  </Button>
                )}

                {/* <Button
                size="small"
                onClick={async () => {
                  await DownloadImage(cnvData.plotAllData,`${t("#Chr")}`)
                }}
              >
                <FileDownloadIcon />
              </Button> */}
              </ButtonGroup>
            </Box>
          </Box>
        ) : (
          ''
        )}

        {!props.onlyChrom &&
        cnvData &&
        cnvData.plotData &&
        cnvData.plotData.length
          ? cnvData.plotData
              .filter((item) => item && item.plot)
              .map((items, index) => {
                return (
                  <Box>
                    <img key={items.chrom} src={items.plot} />

                    <Box width={'100%'} textAlign={'end'} pr={10}>
                      <ButtonGroup
                        variant="contained"
                        aria-label="Basic button group"
                      >
                        {plotlyImgSrc ? (
                          <Button
                            size="small"
                            onClick={() => {
                              const documentCopy = document as any

                              if (documentCopy.exitFullscreen) {
                                documentCopy.exitFullscreen()
                              } else if (documentCopy.mozCancelFullScreen) {
                                // Firefox

                                documentCopy.mozCancelFullScreen()
                              } else if (documentCopy.webkitExitFullscreen) {
                                // Chrome, Safari and Opera

                                documentCopy.webkitExitFullscreen()
                              } else if (documentCopy.msExitFullscreen) {
                                // IE/Edge

                                documentCopy.msExitFullscreen()
                              }

                              setPlotlyImgSrc('')

                              setPlotlyImgChrom('')
                            }}
                          >
                            <CloseFullscreenIcon />
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            onClick={() => {
                              if (plotlyImgRef.current.requestFullscreen) {
                                plotlyImgRef.current.requestFullscreen()
                              } else if (
                                plotlyImgRef.current.mozRequestFullScreen
                              ) {
                                // Firefox

                                plotlyImgRef.current.mozRequestFullScreen()
                              } else if (
                                plotlyImgRef.current.webkitRequestFullscreen
                              ) {
                                // Chrome, Safari & Opera

                                plotlyImgRef.current.webkitRequestFullscreen()
                              } else if (
                                plotlyImgRef.current.msRequestFullscreen
                              ) {
                                // IE/Edge

                                plotlyImgRef.current.msRequestFullscreen()
                              }

                              setPlotlyImgSrc(items.plot)

                              setPlotlyImgChrom(items.chrom)
                            }}
                          >
                            <OpenInFullIcon />
                          </Button>
                        )}
                      </ButtonGroup>
                    </Box>
                  </Box>
                )
              })
          : ''}

        <Box ref={plotlyImgRef}>
          {!props.onlyChrom && plotlyImgSrc ? (
            <Box
              bgcolor={'white'}
              display="flex"
              alignItems={'center'}
              justifyContent="center"
              flexDirection={'column'}
              width={'100%'}
              height={'100%'}
            >
              <Typography fontWeight={500} fontSize={20} color={'primary.main'}>
                {`${t('#Chr')}${plotlyImgChrom}`}
              </Typography>

              <img
                src={plotlyImgSrc}
                width={'100%'}
                style={{
                  height: '40vh'
                }}
              />

              <Box width={'100%'} textAlign={'end'} pr={10}>
                <ButtonGroup
                  variant="contained"
                  aria-label="Basic button group"
                >
                  {plotlyImgSrc ? (
                    <Button
                      size="small"
                      onClick={() => {
                        const documentCopy = document as any

                        if (documentCopy.exitFullscreen) {
                          documentCopy.exitFullscreen()
                        } else if (documentCopy.mozCancelFullScreen) {
                          // Firefox

                          documentCopy.mozCancelFullScreen()
                        } else if (documentCopy.webkitExitFullscreen) {
                          // Chrome, Safari and Opera

                          documentCopy.webkitExitFullscreen()
                        } else if (documentCopy.msExitFullscreen) {
                          // IE/Edge

                          documentCopy.msExitFullscreen()
                        }

                        setPlotlyImgSrc('')

                        setPlotlyImgChrom('')
                      }}
                    >
                      <CloseFullscreenIcon />
                    </Button>
                  ) : (
                    <Button size="small">
                      <OpenInFullIcon />
                    </Button>
                  )}

                  {/* <IconButton>
                <OpenInFullSharp />
              </IconButton> */}
                </ButtonGroup>
              </Box>
            </Box>
          ) : (
            ''
          )}
        </Box>

        <PositionedSnackbar ref={messageRef} />
      </Box>
    )
  }
)

const CnvQcPlot = React.forwardRef(
  (
    props: {
      chrom: string
      handleOpen: any
      plotlyRef: any
      containerId: string
      start?: any
      end?: any
      analysisInfo?: any
      cnvSampleId?: any
    },
    ref
  ) => {
    const router = useRouter()
    const { t } = useTranslation()

    const { id } = router.query
    const hg19_len: NObject = {
      '1': 249250621,
      '2': 243199373,
      '3': 198022430,
      '4': 191154276,
      '5': 180915260,
      '6': 171115067,
      '7': 159138663,
      '8': 146364022,
      '9': 141213431,
      '10': 135534747,
      '11': 135006516,
      '12': 133851895,
      '13': 115169878,
      '14': 107349540,
      '15': 102531392,
      '16': 90354753,
      '17': 81195210,
      '18': 78077248,
      '19': 59128983,
      '20': 63025520,
      '21': 48129895,
      '22': 51304566,
      X: 155270560,
      Y: 59373566
    }
    const [data, setData] = React.useState<any>()
    const [start, setStart] = React.useState<number>(0)
    const [end, setEnd] = React.useState<number>(100000)
    const [chrom, setChrom] = React.useState({
      label: `${t('chromosome')} ${props.chrom}`,
      value: hg19_len[props.chrom],
      key: props.chrom
    })

    const affectId = React.useMemo(() => {
      if (
        props.analysisInfo &&
        props.analysisInfo.samples &&
        Array.isArray(props.analysisInfo.samples)
      ) {
        const samples = props.analysisInfo.samples
        const affected_child = samples.find(
          (i: any) => i.type === 'affected_child'
        )
        if (affected_child) {
          return affected_child.id
        } else return id
      } else return id
    }, [props.analysisInfo])

    const fetchCnvPlot = useMutation({
      mutationFn: async (params: GetSampleCnvQcDto) => {
        if (props.analysisInfo.pipelineVersion) {
          return await getGlsExomedepthPlotData({
            id: (props.cnvSampleId ?? affectId) as string,
            chrom: params.chrom,
            start: params.start,
            end: params.end
          })
        } else
          return await getCnvPlot(
            (props.cnvSampleId ?? affectId) as string,
            params
          )
      },
      onSuccess: (data) => {
        if (data) {
          setData(data)
        }
      },
      onSettled: () => {
        props.handleOpen(false)
      }
    })

    const plotData = React.useMemo(() => {
      const container = document.getElementById(props.containerId)

      if (data) {
        const pdata = [
          {
            x: data.poly1.x,
            y: data.poly1.y,
            fill: 'toself',
            type: 'scatter',
            mode: 'none',
            fillcolor: 'lightgrey',
            line: { color: 'transparent' }
          },
          {
            x: data.poly2.x,
            y: data.poly2.y,
            fill: 'toself',
            type: 'scatter',
            mode: 'none',
            fillcolor: 'lightgrey',
            line: { color: 'lightgrey' }
          },
          {
            x: data.poly3.x,
            y: data.poly3.y,
            fill: 'toself',
            type: 'scatter',
            mode: 'none',
            fillcolor: 'lightgrey',
            line: { color: 'lightgrey' }
          },
          {
            x: data.x_points,
            y: data.y_points,
            type: 'scatter',
            mode: 'lines+markers',
            text: data.text_points, // Display gene names
            textposition: 'top center', // Position of text
            marker: { symbol: 'cross', color: 'red' },
            hovertemplate: '%{y}<br>%{text}<extra></extra>',
            line: { color: 'red' }
          }
        ]

        const xlab = 'Test'
        const ylab = 'Observed by expected read ratio'

        const layout = {
          title: xlab,
          width: container?.clientWidth
            ? props.containerId === 'CnvPlot'
              ? container?.clientWidth - 300
              : container?.clientWidth - 500
            : 700, // Set to desired width
          height: container?.clientHeight
            ? props.containerId === 'CnvPlot'
              ? container?.clientHeight
              : container?.clientHeight - 300
            : 400, // Set to the same value as width to make it square
          xaxis: { range: data.xlimRange, title: '' },
          yaxis: { range: data.ylimRange, title: ylab },
          showlegend: false
        }

        //

        return {
          pdata,
          layout
        }
      } else {
        const pdata = [
          {
            x: [],
            y: [],
            fill: 'toself',
            type: 'scatter',
            mode: 'none',
            fillcolor: 'lightgrey',
            line: { color: 'transparent' }
          },
          {
            x: [],
            y: [],
            fill: 'toself',
            type: 'scatter',
            mode: 'none',
            fillcolor: 'lightgrey',
            line: { color: 'lightgrey' }
          },
          {
            x: [],
            y: [],
            fill: 'toself',
            type: 'scatter',
            mode: 'none',
            fillcolor: 'lightgrey',
            line: { color: 'lightgrey' }
          },
          {
            x: [],
            y: [],
            type: 'scatter',
            mode: 'lines+markers',
            text: [], // Display gene names
            textposition: 'top center', // Position of text
            marker: { symbol: 'cross', color: 'red' },
            hovertemplate: '%{y}<br>%{text}<extra></extra>',
            line: { color: 'red' }
          }
        ]

        const xlab = 'Test'
        const ylab = 'Observed by expected read ratio'

        const layout = {
          title: xlab,
          width: container?.clientWidth
            ? props.containerId === 'CnvPlot'
              ? container?.clientWidth - 300
              : container?.clientWidth - 500
            : 700, // Set to desired width
          height: container?.clientHeight
            ? props.containerId === 'CnvPlot'
              ? container?.clientHeight
              : container?.clientHeight - 300
            : 400, // Set to the same value as width to make it square
          xaxis: { range: [], title: '' },
          yaxis: { range: [], title: ylab },
          showlegend: false
        }

        return {
          pdata,
          layout
        }
      }
    }, [data, props.containerId])

    React.useEffect(() => {
      //
    }, [plotData])

    const handleChange = (event: any, newValue: number | number[]) => {
      // setValue(newValue as number[]);
      if (Array.isArray(newValue)) {
        setStart(newValue[0])

        setEnd(newValue[1])
      }
    }

    const marks = React.useMemo(() => {
      return [
        {
          value: 0,
          label: 0
        },
        {
          value: chrom.value,
          label: chrom.value
        }
      ]
    }, [chrom])

    React.useEffect(() => {
      const params: GetSampleCnvQcDto = {
        chrom: chrom.key as VariantChrom,
        start: props.start ? props.start : start,
        end: props.end ? props.end : end
      }
      if (props.start) setStart(props.start)
      if (props.end) setEnd(props.end)

      props.handleOpen(true)

      //

      if (params.start) fetchCnvPlot.mutateAsync(params)
      else props.handleOpen(false)

      // Plotly.newPlot('CnvPlot', pdata as any, layout)
    }, [props.start, props.end])

    // React.useEffect(() => {
    //   if (props.start && props.end) {
    //     const params: GetSampleCnvQcDto = {
    //       chrom: chrom.key as VariantChrom,
    //       start: props.start,
    //       end: props.end
    //     }

    //     props.handleOpen(true)

    //     fetchCnvPlot.mutateAsync(params)
    //   }

    //   // Plotly.newPlot('CnvPlot', pdata as any, layout)
    // }, [props.start, props.end])

    const searchFn = () => {
      console.log(start, end)
      const params: GetSampleCnvQcDto = {
        chrom: chrom.key as VariantChrom,
        start,
        end
      }

      props.handleOpen(true)

      fetchCnvPlot.mutateAsync(params)
    }

    React.useImperativeHandle(ref, () => {
      return {
        end,
        start,
        searchFn
      }
    })

    return (
      <Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          alignItems="center"
          justifyContent={'center'}
          id={'CnvPlot'}
        >
          {plotData ? (
            <Box>
              <Plot
                data={plotData.pdata as any}
                layout={{
                  ...plotData.layout,
                  title: `${t('chromosome')} ${props.chrom}`,
                  dragmode: 'zoom'
                }}
              />
            </Box>
          ) : (
            ''
          )}

          <Box
            width={700}
            mt={4}
            display="flex"
            alignItems={'center'}
            justifyContent="center"
          >
            <Stack spacing={4} pr={14}>
              <TextField
                size="small"
                id="outlined-number"
                label="start"
                type="number"
                InputLabelProps={{
                  shrink: true
                }}
                value={start}
                onChange={(e) => {
                  const value = Number(e.target.value)

                  setStart(value)
                }}
                onBlur={(e) => {
                  //
                  const value = Number(e.target.value)

                  if (value <= end) {
                    setStart(value)

                    // const params: GetSampleCnvQcDto = {
                    //   chrom: chrom.key as VariantChrom,
                    //   start: value,
                    //   end
                    // }

                    // props.handleOpen(true)

                    // fetchCnvPlot.mutateAsync(params)
                  } else {
                    setStart(0)
                  }
                }}
              />

              <TextField
                size="small"
                id="outlined-number"
                label="end"
                type="number"
                InputLabelProps={{
                  shrink: true
                }}
                value={end}
                onChange={(e) => {
                  const value = Number(e.target.value)

                  setEnd(value)
                }}
                onBlur={(e) => {
                  const value = Number(e.target.value)

                  if (value >= chrom.value) {
                    setEnd(Number(chrom.value))
                  } else if (value < start) {
                    setEnd(Number(chrom.value))
                  } else {
                    setEnd(value)

                    // const params: GetSampleCnvQcDto = {
                    //   chrom: chrom.key as VariantChrom,
                    //   start,
                    //   end: value
                    // }

                    // props.handleOpen(true)

                    // fetchCnvPlot.mutateAsync(params)
                  }
                }}
              />
            </Stack>

            <AirbnbSlider
              value={[start, end]}
              onChange={handleChange}
              // eslint-disable-next-line lines-around-comment
              // onChangeCommitted={(e, newValue) => {
              //   if (Array.isArray(newValue)) {
              //     const start = newValue[0]
              //     const end = newValue[1]
              //     const params: GetSampleCnvQcDto = {
              //       chrom: chrom.key as VariantChrom,
              //       start,
              //       end
              //     }

              //     props.handleOpen(true)

              //     fetchCnvPlot.mutateAsync(params)
              //   }
              // }}
              valueLabelDisplay="auto"
              min={0}
              max={chrom.value}
              marks={marks}
              slots={{ thumb: AirbnbThumbComponent }}
              getAriaLabel={(index) =>
                index === 0 ? 'Minimum price' : 'Maximum price'
              }
            />
          </Box>
        </Box>
      </Box>
    )
  }
)

export default CnvHG19QcPlot
